import {
  TextField,
  OutlinedInput,
  FormControl,
  InputLabel,
  InputAdornment,
  InputBaseComponentProps,
} from "@material-ui/core";
import { compose } from "ramda";
import { memo, NamedExoticComponent, useEffect } from "react";
import { UseFormMethods, Control, Controller } from "react-hook-form";

import { getAddressInformationBasedOnPostalCode } from "../../api";
import { withError } from "../HOC";
import { MaskType, MaskedInput } from "../MaskedInput";
import { Tooltip } from "../Tooltip";
import { CommonFormElProps } from "../types";

export type ZipCodeProps = {
  readonly control: Control;
  readonly register: UseFormMethods["register"];
  readonly setValue: UseFormMethods["setValue"];
  readonly placeholder?: string;
  readonly mask?: MaskType;
  readonly suffix?: string;
  readonly isNumber?: boolean;
  readonly prefix?: string;
  readonly citySelectId?: string;
  readonly tooltip?: string;
} & CommonFormElProps;

export const ZipCode = memo<ZipCodeProps>(
  ({
    control,
    defaultValue = "",
    disabled: isDisabled = false,
    hasError,
    id,
    label: labelTxt,
    mask,
    placeholder,
    prefix,
    register,
    required = false,
    setValue,
    suffix,
    isNumber = false,
    citySelectId = "",
    tooltip,
    onChange: onChangeAction,
  }) => {
    const endAdornment = suffix && (
      <InputAdornment position="end">{suffix}</InputAdornment>
    );
    const startAdornment = prefix && (
      <InputAdornment position="start">{prefix}</InputAdornment>
    );
    const type = (isNumber && "number") || "text";
    const label = (
      <>
        {labelTxt}
        <Tooltip copy={tooltip} />
      </>
    );

    useEffect(() => {
      setValue(id, defaultValue);

      if (defaultValue === "00-000" && onChangeAction) {
        onChangeAction([]);
      }
    }, [defaultValue, id, onChangeAction, setValue]);

    if (!mask) {
      return (
        <TextField
          autoComplete="off"
          defaultValue={defaultValue}
          disabled={isDisabled}
          error={hasError}
          id={id}
          inputRef={register}
          InputProps={{
            name: id,
            endAdornment,
            notched: false,
            placeholder,
            startAdornment,
            type,
          }}
          InputLabelProps={{
            disabled: isDisabled,
            required: !isDisabled && required,
            shrink: true,
          }}
          label={label}
          variant="outlined"
        />
      );
    }

    const isZipCodeValid = (zipCode: string) => {
      let intCount = 0;
      for (let i = 0; i < zipCode.length; i++) {
        if (!isNaN(parseInt(zipCode[i]))) {
          intCount++;
        }
      }

      return intCount === 5;
    };

    const onZipCodeChange = async (value: string) => {
      if (value === "00-000") {
        onChangeAction([]);
        return;
      }

      if (isZipCodeValid(value)) {
        const addressData = await getAddressInformationBasedOnPostalCode(value);
        onChangeAction(addressData?.cities || []);
      } else {
        onChangeAction([]);
      }
    };

    return (
      <FormControl error={hasError}>
        <InputLabel
          shrink
          disabled={isDisabled}
          error={hasError}
          htmlFor={id}
          required={!isDisabled && required}
        >
          {label}
        </InputLabel>
        <Controller
          name={id}
          control={control}
          defaultValue={defaultValue}
          render={({ onChange, ...props }) => (
            <OutlinedInput
              autoComplete="off"
              defaultValue={defaultValue}
              disabled={isDisabled}
              endAdornment={endAdornment}
              error={hasError}
              id={id}
              inputComponent={
                MaskedInput as NamedExoticComponent<InputBaseComponentProps>
              }
              inputProps={{
                mask,
                name: id,
                placeholder,
                type,
              }}
              onKeyUp={(e) => {
                const newValue = e.currentTarget.value;
                setValue(id, newValue);
              }}
              onChange={(e) => onZipCodeChange(e.currentTarget.value)}
              startAdornment={startAdornment}
            />
          )}
        ></Controller>
      </FormControl>
    );
  }
);

export const ZipCodeWithError = compose(withError)(ZipCode);
